import { useEffect, useRef } from "react";
import { Popover, PopoverButton, PopoverPanel } from "@headlessui/react";
import { format, parseISO } from "date-fns";
import { Calendar2 } from "react-bootstrap-icons";
import { cn } from "@utils";
import { Calendar } from "@components/datePickers/components/Calendar";

const HOURS = Array.from({ length: 24 }, (_, i) => String(i).padStart(2, "0"));
const MINUTES = Array.from({ length: 60 }, (_, i) => String(i).padStart(2, "0"));

// A single compact trigger ("Wed 9 Sep, 09:00") whose popover holds the Ontik-themed calendar and a
// custom hour/minute picker together. The value is a full "yyyy-MM-ddTHH:mm" (the time part is kept
// even in date-only mode, so toggling all-day back on doesn't lose it). The panel is anchored
// (Headless UI v2 portals it to the body) so it escapes any scroll/overflow container and flips to
// stay on-screen. `dateOnly` hides the time row (for all-day items).
export default function DateTimePicker({
  value,
  onChange,
  disabled,
  dateOnly = false,
  placeholder = "Pick a date & time",
  invalid = false,
  anchor = "bottom start",
}) {
  const datePart = value?.slice(0, 10) ?? "";
  const timePart = value && value.length >= 16 ? value.slice(11, 16) : "09:00";
  const selectedDate = datePart ? parseISO(datePart) : null;

  const setDate = (date) => {
    if (date) onChange(`${format(date, "yyyy-MM-dd")}T${timePart}`);
  };
  const setTime = (time) => {
    const base = datePart || format(new Date(), "yyyy-MM-dd");
    onChange(`${base}T${time}`);
  };

  const label = selectedDate
    ? dateOnly
      ? format(selectedDate, "EEE d MMM yyyy")
      : `${format(selectedDate, "EEE d MMM")}, ${timePart}`
    : placeholder;

  return (
    <Popover className="w-full">
      <PopoverButton
        disabled={disabled}
        className={cn(
          "flex w-full items-center gap-2 rounded-lg border bg-white px-3 py-2 text-sm text-zinc-900 hover:bg-zinc-50 focus:outline focus:outline-1 focus:-outline-offset-1 focus:outline-primary disabled:bg-zinc-50 disabled:text-zinc-400",
          invalid ? "border-red-300" : "border-zinc-300",
        )}
      >
        <Calendar2 className="size-4 shrink-0 text-zinc-500" />
        <span className={cn("truncate", !selectedDate && "text-zinc-400")}>{label}</span>
      </PopoverButton>
      <PopoverPanel
        anchor={anchor}
        // calendar-container opts out of the global td/th:first/last-child edge padding in index.css
        // (which otherwise squashes the calendar's Sun/Sat columns), matching DatePicker/DateRangePicker.
        className="calendar-container z-50 rounded-xl border border-zinc-200 bg-white p-2 shadow-lg [--anchor-gap:0.4rem]"
      >
        {/* No autoFocus: the panel is portaled to the end of <body>, so focusing a day on open makes
            the browser scroll the page down to reach it (then back) — jarring behind the modal. */}
        <Calendar mode="single" selected={selectedDate} onSelect={setDate} />
        {!dateOnly && (
          // A custom hour/minute picker rather than a native <input type="time"> — the native time
          // pop-out draws its own (blue) selection CSS can't reach. These columns reuse the calendar's
          // selected style (bg-primary), so the whole control reads as one Ontik-orange picker.
          <div className="mt-1.5 flex items-center gap-2 border-t border-zinc-100 px-1 pt-1.5">
            <span className="text-xs font-medium text-zinc-500">Time</span>
            <div className="ml-auto flex items-center gap-1">
              <TimeColumn
                items={HOURS}
                selected={timePart.slice(0, 2)}
                onSelect={(h) => setTime(`${h}:${timePart.slice(3, 5)}`)}
              />
              <span className="text-sm font-medium text-zinc-300">:</span>
              <TimeColumn
                items={MINUTES}
                selected={timePart.slice(3, 5)}
                onSelect={(m) => setTime(`${timePart.slice(0, 2)}:${m}`)}
              />
            </div>
          </div>
        )}
      </PopoverPanel>
    </Popover>
  );
}

function TimeColumn({ items, selected, onSelect }) {
  const scrollRef = useRef(null);
  // Centre the selected value when the popover opens, so it's in view rather than buried below a
  // scroll. `relative` makes this container the offsetParent; a rAF defers the read until the anchored
  // panel has been laid out.
  useEffect(() => {
    const frame = requestAnimationFrame(() => {
      const container = scrollRef.current;
      const active = container?.querySelector('[data-selected="true"]');
      if (container && active) {
        container.scrollTop = active.offsetTop - container.clientHeight / 2 + active.clientHeight / 2;
      }
    });
    return () => cancelAnimationFrame(frame);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div
      ref={scrollRef}
      className="relative h-28 w-9 overflow-y-auto rounded-md border border-zinc-200 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
    >
      <div className="flex flex-col p-0.5">
        {items.map((item) => (
          <button
            key={item}
            type="button"
            data-selected={item === selected}
            onClick={() => onSelect(item)}
            className={cn(
              "flex h-8 items-center justify-center rounded-md p-0 text-sm font-normal transition-colors",
              item === selected
                ? "bg-primary text-primary-foreground"
                : "text-zinc-600 hover:bg-accent hover:text-accent-foreground",
            )}
          >
            {item}
          </button>
        ))}
      </div>
    </div>
  );
}
